import type { Translator } from "./validationHelpers";
import {
  BENEFICIARY_CULTURAL_ACTIVITIES,
  type CompanyType,
  type CulturalActivity,
  keepActivitiesForIncentive,
} from "@/domain/beneficiary";
import type { TaxIncentiveTypeValue } from "@/domain/classification/constants";
import { isValidIban } from "@/domain/iban";
import {
  createRepresentativeSchema,
  createRepresentativeValidationMessages,
  EMPTY_REPRESENTATIVE,
  type RepresentativeValidationMessages,
} from "./representativeSchemas";
import { JOINT_MAX_SIGNERS, JOINT_MIN_SIGNERS, SIGNATURE_REGIMES } from "@/domain/investor";
import * as yup from "yup";

/**
 * ---------------------------------------------------------------------------
 * Shared field builders (DRY between edit + create)
 * ---------------------------------------------------------------------------
 */

export interface BeneficiaryValidationMessages {
  required: string;
  emailInvalid: string;
  ibanInvalid: string;
  ibanRequiredWithBank: string;
  representative: RepresentativeValidationMessages;
  dniInvalid: string;
  minActivities: string;
  activitiesIncentiveMismatch: string;
  jointMinSigners: string;
  jointMaxSigners: string;
  jointSignerEmailRequired: string;
}

const INCENTIVE_TYPE_VALUES = ["film_audiovisual", "performing_arts_music"] as const;

const COMPANY_TYPE_VALUES = [
  "freelancer",
  "limited_company",
  "public_company",
  "association",
  "foundation",
  "cooperative",
  "other",
] as const;

function requiredString(msg: string) {
  return yup.string().required(msg).trim().min(1, msg);
}

function optionalString() {
  return yup.string().defined().default("");
}

function dniField(msgs: Pick<BeneficiaryValidationMessages, "required" | "dniInvalid">) {
  return yup
    .string()
    .required(msgs.required)
    .trim()
    .min(1, msgs.required)
    .matches(/^[A-Za-z0-9-]+$/, msgs.dniInvalid);
}

function emailField(msg: string) {
  return yup.string().email(msg).defined().default("");
}

function legalRepShape(msgs: BeneficiaryValidationMessages) {
  return {
    first_name: requiredString(msgs.required),
    last_name: requiredString(msgs.required),
    dni: dniField(msgs),
    position: optionalString(),
    phone: optionalString(),
    email: emailField(msgs.emailInvalid),
  };
}

/**
 * ---------------------------------------------------------------------------
 * Profile Edit schema
 * ---------------------------------------------------------------------------
 */

export function createBeneficiaryEditSchema(msgs: BeneficiaryValidationMessages) {
  return yup.object({
    // Legal & fiscal
    name: requiredString(msgs.required),
    tax_id: requiredString(msgs.required),
    company_type: requiredString(msgs.required).oneOf([...COMPANY_TYPE_VALUES], msgs.required),
    incentive_type: yup
      .string()
      .oneOf([...INCENTIVE_TYPE_VALUES, ""])
      .nullable()
      .defined()
      .default(null),
    constitution_date: optionalString(),

    // Business profile
    main_activities: yup
      .array()
      .of(
        yup
          .string()
          .oneOf(BENEFICIARY_CULTURAL_ACTIVITIES, msgs.minActivities)
          .required(msgs.minActivities),
      )
      .min(1, msgs.minActivities)
      .required(msgs.minActivities),
    cnae_code: optionalString(),
    business_purpose: optionalString(),
    employee_count: yup.number().nullable().defined().default(null),
    last_year_revenue: optionalString(),

    // Location & contact
    address: optionalString(),
    city: optionalString(),
    postal_code: optionalString(),
    state: optionalString(),
    country: optionalString(),
    phone: optionalString(),
    email: emailField(msgs.emailInvalid),
    website: optionalString(),

    // JOINT signing rules mirror what the backend enforces at dispatch time.
    signature_regime: yup
      .string()
      .oneOf([...SIGNATURE_REGIMES])
      .defined()
      .default("sole"),

    // Legal representatives (field array)
    legal_representatives: yup
      .array()
      .of(
        yup.object({
          // Empty for freshly added rows — the save flow creates them via POST.
          uuid: optionalString(),
          is_primary: yup.boolean().defined().default(true),
          is_signer: yup.boolean().defined().default(true),
          ...legalRepShape(msgs),
        }),
      )
      .min(1, msgs.required)
      .required(msgs.required)
      .test("joint-min-signers", msgs.jointMinSigners, function (value) {
        if (this.parent.signature_regime !== "joint") return true;
        return signersOf(value).length >= JOINT_MIN_SIGNERS;
      })
      .test("joint-max-signers", msgs.jointMaxSigners, function (value) {
        if (this.parent.signature_regime !== "joint") return true;
        return signersOf(value).length <= JOINT_MAX_SIGNERS;
      })
      .test("joint-signer-email", msgs.jointSignerEmailRequired, function (value) {
        if (this.parent.signature_regime !== "joint") return true;
        return signersOf(value).every((rep) => !!rep.email?.trim());
      }),
  });
}

function signersOf(value: unknown) {
  return ((value ?? []) as Array<{ is_signer?: boolean; email?: string }>).filter(
    (rep) => rep.is_signer,
  );
}

export type BeneficiaryEditFormData = yup.InferType<ReturnType<typeof createBeneficiaryEditSchema>>;

/**
 * ---------------------------------------------------------------------------
 * Creation Wizard schema
 * ---------------------------------------------------------------------------
 */

export function createBeneficiaryWizardSchema(msgs: BeneficiaryValidationMessages) {
  const blank = (value: unknown) => typeof value !== "string" || value.trim() === "";
  return yup.object({
    name: yup.string().defined().default("").trim().required(msgs.required),
    taxId: yup.string().defined().default("").trim().required(msgs.required),
    constitutionDate: optionalString(),
    companyType: yup
      .mixed<CompanyType>()
      .oneOf([...COMPANY_TYPE_VALUES])
      .nullable()
      .defined()
      .default(null)
      .test("required", msgs.required, (value) => value !== null),
    incentiveType: yup
      .mixed<TaxIncentiveTypeValue>()
      .oneOf([...INCENTIVE_TYPE_VALUES])
      .nullable()
      .defined()
      .default(null)
      .test("required", msgs.required, (value) => value !== null),

    activities: yup
      .array()
      .of(yup.mixed<CulturalActivity>().defined())
      .defined()
      .default([])
      .min(1, msgs.minActivities)
      .test(
        "fit-incentive",
        msgs.activitiesIncentiveMismatch,
        (activities, context) =>
          keepActivitiesForIncentive(activities, context.parent.incentiveType).length ===
          activities.length,
      ),
    cnae: optionalString(),
    businessPurpose: optionalString(),
    employees: optionalString(),
    revenue: optionalString(),

    address: optionalString(),
    city: optionalString(),
    postalCode: optionalString(),
    province: optionalString(),
    country: optionalString(),
    phone: optionalString(),
    email: emailField(msgs.emailInvalid),
    website: optionalString(),

    representative: createRepresentativeSchema(msgs.representative),

    bankName: optionalString(),
    iban: optionalString()
      .test("iban-valid", msgs.ibanInvalid, (value) => blank(value) || isValidIban(value ?? ""))
      .test("iban-with-bank", msgs.ibanRequiredWithBank, function (value) {
        return !blank(value) || (blank(this.parent.bankName) && blank(this.parent.swift));
      }),
    swift: optionalString(),
  });
}

export type BeneficiaryWizardFormData = yup.InferType<
  ReturnType<typeof createBeneficiaryWizardSchema>
>;

export const EMPTY_BENEFICIARY_WIZARD: BeneficiaryWizardFormData = {
  name: "",
  taxId: "",
  constitutionDate: "",
  companyType: null,
  incentiveType: null,
  activities: [],
  cnae: "",
  businessPurpose: "",
  employees: "",
  revenue: "",
  address: "",
  city: "",
  postalCode: "",
  province: "",
  country: "ES",
  phone: "",
  email: "",
  website: "",
  representative: EMPTY_REPRESENTATIVE,
  bankName: "",
  iban: "",
  swift: "",
};

export const WIZARD_STEP_FIELDS = {
  legal: ["name", "taxId", "constitutionDate", "companyType", "incentiveType"],
  business: ["activities", "cnae", "businessPurpose", "employees", "revenue"],
  location: ["address", "city", "postalCode", "province", "country", "phone", "email", "website"],
  representative: ["representative"],
  bank: ["bankName", "iban", "swift"],
} as const satisfies Record<string, readonly (keyof BeneficiaryWizardFormData)[]>;

export const BENEFICIARY_WIZARD_STEP_IDS = [
  "legal",
  "business",
  "location",
  "representative",
  "bank",
] as const satisfies readonly (keyof typeof WIZARD_STEP_FIELDS)[];

/**
 * ---------------------------------------------------------------------------
 * Validation messages factory (i18n)
 * ---------------------------------------------------------------------------
 */

/**
 * Minimal translator interface — accepts any callable that maps a key to a string.
 * Avoids importing i18next in the domain layer.
 */

export function createBeneficiaryValidationMessages(t: Translator): BeneficiaryValidationMessages {
  return {
    required: t("validation.required"),
    emailInvalid: t("validation.email_invalid"),
    ibanInvalid: t("validation.iban_invalid"),
    ibanRequiredWithBank: t("validation.iban_required_with_bank"),
    representative: createRepresentativeValidationMessages(t),
    dniInvalid: t("validation.dni_invalid"),
    minActivities: t("validation.min_activities"),
    activitiesIncentiveMismatch: t("validation.activities_incentive_mismatch"),
    jointMinSigners: t("validation.joint_min_signers"),
    jointMaxSigners: t("validation.joint_max_signers"),
    jointSignerEmailRequired: t("validation.joint_signer_email_required"),
  };
}
